import {
  Badge,
  Button,
  Col,
  Form,
  Input,
  Row,
  Space,
  Table,
  Tooltip,
} from 'antd';
import Search from 'antd/es/input/Search';
import { ColumnsType } from 'antd/es/table';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DateUtils from '../../../utils/date.utils';
import showConfirmModal from '../../common/modal-confirm';
import showNotification from '../../common/notification';
import PaginationComponent from '../../common/pagination/component/pagination';
import './index.scss';
import { ApiManagementApi } from '../../../apis/api-management.api';
import { ApiManagement } from '../../../models/api-management';
import ModalApiManagement from './modal-form';
import InsertPasscodeForm from '../../common/modal-mfa/modal-input-passcode';
import { DefaultGUID } from '@/constants/app-constants';
import { CopyOutlined } from '@ant-design/icons';
import { right } from 'devexpress-reporting/scopes/reporting-chart-internal';

function ApiManagementForm() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [pageSize, setPageSize] = useState(10);
  const [pageNumber, setPageNumber] = useState(1);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalVerifyOpen, setIsModalVerifyOpen] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [ApiManagements, setApiManagement] = useState<ApiManagement[]>([]);
  const [verifyForm] = Form.useForm();
  const [dataForModal, setDataForModal] = useState<ApiManagement>();

  useEffect(() => {
    handleGetAllAccessFilter();
  }, []);

  const handleGetAllAccessFilter = async (
    pageSizeData?: number,
    pageNumberData?: number,
    searchString?: string
  ) => {
    pageSizeData = pageSizeData || pageSize;
    pageNumberData = pageNumberData || pageNumber;
    searchString = searchString || '';
    setIsLoading(true);
    try {
      const { data } = await ApiManagementApi.getApiManagementsByFilter(
        pageNumberData,
        pageSizeData,
        searchString
      );

      const ApiManagementsWithVisibility = data.records.map(
        (resource: ApiManagement) => ({
          ...resource,
          isVisible: false,
          appSecret: generateAppSecretKey(),
        })
      );

      setApiManagement(ApiManagementsWithVisibility);
      setIsLoading(false);
      setTotalRecords(data.totalRecords);
    } catch (error) {}
  };

  function generateAppSecretKey(length = 32): string {
    const charset =
      'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let appSecretKey = '';

    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * charset.length);
      appSecretKey += charset[randomIndex];
    }

    return appSecretKey;
  }

  const tableScroll = {
    x: 'max-content',
  };

  const handleCloseModal = () => {
    handleGetAllAccessFilter();
    setIsModalOpen(!isModalOpen);
  };

  const handleClickDelete = (id: string): void => {
    const handleConfirm = async () => {
      try {
        let { data: deleteResponse } =
          await ApiManagementApi.deleteApiManagement(id);
        if (deleteResponse) {
          showNotification('success', t('modal.noti_deleted_success'));
          await handleGetAllAccessFilter();
        }
      } catch (error) {}
    };

    const handleCancel = () => {
      console.log('Cancelled action');
    };

    showConfirmModal({
      type: 'delete',
      title: `Delete this API Key now`,
      content: t('modal.confirm_text_delete'),
      onOk: handleConfirm,
      onCancel: handleCancel,
    });
  };

  const handleClickActive = (id: string, isDeactivate?: boolean): void => {
    let title = isDeactivate ? 'Active' : 'Deactive';

    const handleConfirm = async () => {
      try {
        let { data: activeResponse } =
          await ApiManagementApi.activeApiManagement(id, isDeactivate);
        if (activeResponse) {
          showNotification('success', t('api_management.noti.update_success'));
          await handleGetAllAccessFilter();
        }
      } catch (error) {}
    };

    const handleCancel = () => {};

    showConfirmModal({
      type: 'warning',
      title: `${title} this API Key now`,
      content: t('modal.confirm_text_delete'),
      onOk: handleConfirm,
      onCancel: handleCancel,
    });
  };

  const renderAppSecertText = (record: any) => {
    return (
      <>
        <span>
          <Input.Password
            value={record.appSecret}
            readOnly
            style={{width: '85%'}}
            visibilityToggle={{
              visible: record.isVisible,
              onVisibleChange: (visible) =>
                onVisibleVerifyModal(record.isVisible, record.id),
            }}
          />
        </span>
        <Button
          style={{ float: 'right' }}
          onClick={() =>
            handleCopyAppSecret(
              'appSecret',
              record.appSecret,
              record.isVisible,
              record.id
            )
          }
          icon={<CopyOutlined />}
        />
      </>
    );
  };

  async function openModalForEdit(id: string) {
    if (id === undefined) {
      showNotification('error', 'This Access Resource is not existing');
    } else {
      const { data } = await ApiManagementApi.getApiManagementById(id);
      data.appSecret = generateAppSecretKey();
      setDataForModal(data);
      setIsModalOpen(true);
    }
  }

  async function openModalForCreate() {
    setDataForModal(undefined);
    setIsModalOpen(true);
  }

  function handleCopyData(fieldName: string, data: string) {
    navigator.clipboard
      .writeText(data)
      .then(() => {
        showNotification('success', `Copied ${fieldName} to clipboard`);
      })
      .catch(() => {
        showNotification('error', `Failed to copy: ${fieldName}`);
      });
  }

  function handleCopyAppSecret(
    fieldName: string,
    record: string,
    recordVisibleStatus: boolean,
    recordId: string
  ) {
    if (recordVisibleStatus === false) {
      onVisibleVerifyModal(recordVisibleStatus, recordId);
    } else {
      handleCopyData(fieldName, record);
    }
  }

  const columns: ColumnsType<ApiManagement> = [
    {
      title: t('api_management.title.name'),
      key: '1',
      render: (text: string, record: any) => <>{record.name}</>,
    },
    {
      title: t('api_management.title.appKey'),
      key: '2',
      render: (text: string, record: any) => (
        <>
          <span> {record.appKey}</span>
          <Button
            style={{ float: 'right' }}
            onClick={() => handleCopyData('appKey', record.appKey)}
            icon={<CopyOutlined />}
          />
        </>
      ),
    },
    {
      title: t('api_management.title.appSecret'),
      key: '3',
      className: 'col-status',
      dataIndex: 'isDeactivate',
      render: (text: string, record: any) => {
        return <>{renderAppSecertText(record)}</>;
      },
    },
    {
      title: t('api_management.title.status'),
      key: '4',
      className: 'col-status',
      dataIndex: 'isDeactivate',
      render: (isDeactivate) => {
        return (
          <>
            {isDeactivate && (
              <Badge
                status="error"
                text="Deactivate"
              />
            )}
            {!isDeactivate && (
              <Badge
                status="success"
                text="Activate"
              />
            )}
          </>
        );
      },
    },
    {
      title: t('api_management.title.fromDate'),
      key: '5',
      className: 'col-status',
      dataIndex: 'isDeactivate',
      render: (text: string, record: any) => {
        return (
          <>
            <Row> {DateUtils.isoDate(record.fromDate)}</Row>
          </>
        );
      },
    },
    {
      title: t('api_management.title.toDate'),
      key: '6',
      className: 'col-status',
      dataIndex: 'isDeactivate',
      render: (text: string, record: any) => {
        return (
          <>
            <Row> {DateUtils.isoDate(record.toDate)}</Row>
          </>
        );
      },
    },
    {
      key: '7',
      className: 'col-action',
      render: (_, record) => (
        <Space
          size={[6, 12]}
          wrap
          className="action-cpn">
          <Tooltip title={record.isDeactivate ? 'Active' : 'Deactive'}>
            <Button
              className={
                record.isDeactivate
                  ? 'action-grp-deactive-btn'
                  : 'action-grp-active-btn'
              }
              icon={
                <i
                  className={
                    record.isDeactivate ? 'icon-ok-1' : 'icon-cancel-1'
                  }
                />
              }
              onClick={() =>
                handleClickActive(record.id!.toString(), record.isDeactivate)
              }>
              {record.isDeactivate ? (
                <span className="active">{t('sysConfig.button.active')}</span>
              ) : (
                <span className="deactive">
                  {t('sysConfig.button.deactive')}
                </span>
              )}
            </Button>
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              className="action-grp-delete-btn"
              icon={
                <i
                  className="icon-delete"
                  onClick={() => handleClickDelete(record.id!.toString())}
                />
              }
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const onVisibleVerifyModal = (visible?: boolean, recordId?: string) => {
    if (!visible) {
      setIsModalVerifyOpen(!isModalVerifyOpen);
      verifyForm.setFieldValue('recordId', recordId);
    } else {
      visibleAppSercetAfterVerification(recordId, false);
    }
  };

  const visibleAppSercetAfterVerification = async (
    recordId?: string,
    visible: boolean = true
  ) => {
    const index = ApiManagements.findIndex(
      (resource) => resource.id === recordId
    );

    if(!recordId){
      setIsModalVerifyOpen(!isModalVerifyOpen);
      return;
    }

    try {
      const appSecretKey = await ApiManagementApi.getCurrentAppSercet(
        recordId ?? DefaultGUID
      );
      if (index !== -1) {
        setApiManagement((prevApiManagements) => [
          ...prevApiManagements.slice(0, index),
          {
            ...prevApiManagements[index],
            appSecret: appSecretKey.data,
            isVisible: visible,
          },
          ...prevApiManagements.slice(index + 1),
        ]);
      }
    } catch (error) {
      showNotification('error', t('api_management.noti.error_get_api'));
    }
  };

  const onSearch = async (value : string) => {
    setIsLoading(true);
    try {
      await handleGetAllAccessFilter(pageSize, pageNumber, value);
    } catch (error) {
      showNotification('error', t('api_management.noti.error_get_api'));
    } finally {
      setIsLoading(false);
    }
  }


  return (
    <>
      <div className="ctn-user-index">
        <Row>
          <Col
            className="table-info"
            span={12}>
            <Row className="h4-semiBold">
              {t('menuDashboard.apiManagement')}
            </Row>
            <Row className="body-regular">
              {totalRecords} {t('api_management.title.registed')}
            </Row>
          </Col>
          <Col
            span={12}
            className="table-cmp">
            <Button
              className="btn-create btn-base btn-main-base"
              onClick={() => openModalForCreate()}>
              {t('api_management.button_title.create')}
            </Button>
            <Search
              className="search-cmp"
              placeholder="Search"
              onSearch={onSearch}
              size="large"
              style={{ maxWidth: 400 }}
            />
          </Col>
          <Col span={24}>
            <Row>
              <Col span={24}>
                <Table
                  loading={isLoading}
                  className="tb-list-user"
                  columns={columns}
                  dataSource={ApiManagements}
                  scroll={tableScroll}
                  pagination={{
                    pageSize: pageSize,
                    current: pageNumber,
                    style: { display: 'none' },
                  }}
                />
              </Col>
              <PaginationComponent
                totalRecords={totalRecords}
                pageSize={pageSize}
                setPageSize={setPageSize}
                pageNumber={pageNumber}
                setPageNumber={setPageNumber}
                handleGetFilter={handleGetAllAccessFilter}
              />
            </Row>
          </Col>
        </Row>
      </div>
      <ModalApiManagement
        visible={isModalOpen}
        onClose={handleCloseModal}
        data={dataForModal ?? undefined}
      />
      <InsertPasscodeForm
        visible={isModalVerifyOpen}
        onClose={onVisibleVerifyModal}
        formSubmit={verifyForm}
        usingcase="check-password"
        onCallBack={visibleAppSercetAfterVerification}
      />
    </>
  );
}
export default ApiManagementForm;
